import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  Select,
  MenuItem,
  FormControl,
  TextField,
  Box,
  IconButton
} from '@mui/material';
import { styled } from '@mui/system';
import Autosuggest from 'react-autosuggest';
import ThaiFlag from './thai-flag.png';
import EnglishFlag from './english-flag.png';
import ChinaFlag from './china-flag.png';
import Typesense from 'typesense';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';
import Logo from '../../pages/logo_no_bg.png';
import { useBranding } from '../Header/useBranding';
import { onExplicitUiLangSwitch } from '../../services/langPrefs';


// Styled components for header
const HeaderContainer = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  zIndex: 1000,
  width: '100%',
  backgroundColor: '#fff',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  padding: '12px 0',
  fontFamily: "'Montserrat', 'Poppins', sans-serif",
}));

const HeaderContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '10px',
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '0 20px',
}));

const LogoImage = styled('img')(({ theme }) => ({
  height: '40px',
  marginRight: '15px',
  cursor: 'pointer',
  transition: 'transform 0.2s ease',
  '&:hover': {
    transform: 'scale(1.05)',
  },
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  flexGrow: 1,
  minWidth: '200px',
  maxWidth: '350px',
}));

const ControlsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  flexWrap: 'wrap',
}));

const FlagButton = styled(IconButton)(({ isActive, theme }) => ({
  padding: '4px',
  border: isActive ? '2px solid #3a7bd5' : '2px solid transparent',
  borderRadius: '50%',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.1)',
  },
}));

// const BrandText = styled(Typography)(({ theme }) => ({
//   fontWeight: 700,
//   fontSize: '1.3rem',
//   marginRight: '15px',
//   background: 'linear-gradient(135deg, #3a7bd5, #00d2ff)',
//   WebkitBackgroundClip: 'text',
//   WebkitTextFillColor: 'transparent',
//   display: {
//     xs: 'none',
//     sm: 'block'
//   }
// }));

// Autosuggest styles
const autosuggestStyles = {
  container: {
    position: 'relative',
    width: '100%',
  },
  input: {
    width: '100%',
    padding: '8px 12px',
    paddingRight: '32px',
    borderRadius: '20px',
    border: '1px solid #ccc',
    fontSize: '16px',
    backgroundColor: '#ffffff',
    height: '40px',
    boxSizing: 'border-box',
    fontFamily: "'Montserrat', 'Poppins', sans-serif",
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  },
  suggestionsContainer: {
    display: 'block',
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 1000,
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    borderRadius: '8px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    maxHeight: '300px',
    overflowY: 'auto',
    marginTop: '4px',
  },
  suggestionsList: {
    margin: 0,
    padding: 0,
    listStyleType: 'none',
  },
  suggestion: {
    padding: '10px 16px',
    cursor: 'pointer',
    color: '#333',
    fontSize: '15px',
    fontFamily: "'Montserrat', 'Poppins', sans-serif",
    borderBottom: '1px solid #f0f0f0',
  },
  suggestionHighlighted: {
    backgroundColor: '#f8fafc',
  }
};

// Validate environment variables
const TYPESENSE_HOST = process.env.REACT_APP_TYPESENSE_HOST || 'localhost';
const TYPESENSE_PORT = parseInt(process.env.REACT_APP_TYPESENSE_PORT || '443', 10);
const TYPESENSE_PROTOCOL = process.env.REACT_APP_TYPESENSE_PROTOCOL || 'https';
const TYPESENSE_API_KEY = process.env.REACT_APP_TYPESENSE_API_KEY || '';

// Initialize Typesense client
let typesenseClient;
try {
  typesenseClient = new Typesense.Client({
    nodes: [
      {
        host: TYPESENSE_HOST,
        port: TYPESENSE_PORT,
        protocol: TYPESENSE_PROTOCOL
      }
    ],
    apiKey: TYPESENSE_API_KEY,
    connectionTimeoutSeconds: 5,
    retryIntervalSeconds: 0.1,
    numRetries: 3
  });
} catch (error) {
  console.error('Failed to initialize Typesense client:', error);
  typesenseClient = null;
}

// Helper for date formatting
const formatDateToString = (date) => {
  if (!date) return '';
  
  if (typeof date === 'string') return date;
  
  if (date instanceof Date) {
    try {
      return date.toISOString().split('T')[0];
    } catch (e) {
      console.error('Error converting date to string:', e);
      return '';
    }
  }
  
  return String(date);
};

const EventDetailHeader = () => {
  const { language, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  const branding = useBranding();
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isShowingSuggestions, setIsShowingSuggestions] = useState(false);
  const [searchError, setSearchError] = useState(null);
  
  // Filter states
  const [type, setType] = useState('Category');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  
  // Format dates consistently
  const startDateString = useMemo(() => formatDateToString(startDate), [startDate]);
  const endDateString = useMemo(() => formatDateToString(endDate), [endDate]);

  // Set default dates on mount
  useEffect(() => {
    const today = new Date();
    const defaultStart = today.toISOString().split('T')[0];
    const defaultEnd = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
  }, []);

  const eventTypes = {
    EN: ['Category', 'Product & Service Fair', 'Promotions', 'Arts & Films', '', 'Sports & Competitions', 'Travel & Photos', 'Business & Investment', 'Education & Family', 'Casual Meetups',' Other'],
    TH: ['หมวดหมู่', 'งานแสดงสินค้าและบริการ', 'โปรโมชั่นและส่วนลด', 'ศิลปะและภาพยนตร์', 'คอนเสิร์ตและการแสดงสด', 'กีฬาและการแข่งขัน', 'ท่องเที่ยวและภาพถ่าย', 'ธุรกิจและการลงทุน', 'การศึกษาและครอบครัว', 'อื่นๆ'],
    CN: ['类别', '产品与服务展', '促销活动', '艺术与电影', '现场表演', '体育与竞赛', '旅行与摄影', '商业与投资', '教育与家庭', '其他']
  };

  // Navigate to home page when logo is clicked
  const handleLogoClick = () => {
    // Get the current location state to check if user came from within the app
    const locationState = window.history.state;
    
    // Check if there's a previous page in the browser history and user navigated within the app
    if (window.history.length > 1 && locationState) {
      // Go back to the previous page
      navigate(-1);
    } else {
      // If no previous page or user came directly to this page, navigate to home/map page
      navigate('/');
    }
  };

  // Clear search function
  const clearSearchTerm = () => {
    setSearchTerm('');
    setSuggestions([]);
    setIsShowingSuggestions(false);
  };

  // Build search filters
  const buildFilters = () => {
    const filters = [];
    
    // Language filter
    if (language) {
      filters.push(`language:="${language}"`);
    }

    // Type filter
    if (type && type !== 'Category') {
      filters.push(`type:="${type}"`);
    }

    // Date range filter
    const start = startDateString ? Math.floor(new Date(startDateString).setHours(0, 0, 0, 0) / 1000) : null;
    const end = endDateString ? Math.floor(new Date(endDateString).setHours(23, 59, 59, 999) / 1000) : null;
    
    if (start) filters.push(`event_end_date_ts:>=${start}`);
    if (end) filters.push(`event_date_ts:<=${end}`);

    return filters;
  };

  // Build search parameters
  const buildSearchParams = (query) => {
    const params = {
      q: query || '*',
      query_by: 'event_name,searchableContent',
      per_page: 10,
      typo_tokens_threshold: 1,
      num_typos: 1,
      prefix: true,
      sort_by: '_text_match:desc'
    };

    // Add filters
    const filters = buildFilters();
    if (filters.length) {
      params.filter_by = filters.join(' && ');
    }
    
    return params;
  };

  // Get suggestions function
  const getSuggestions = useCallback(async (value) => {
    if (!value.trim() || !typesenseClient) {
      return setSuggestions([]);
    }
    
    setSearchError(null);
    const params = buildSearchParams(value);
    
    try {
      const res = await typesenseClient.collections('events').documents().search(params);
      
      if (!res || !Array.isArray(res.hits)) {
        setSuggestions([]);
        return;
      }
      
      const hits = res.hits.map((hit) => hit.document).filter(Boolean);
      
      // Filter duplicates
      const seen = new Set();
      const unique = hits.filter((doc) => {
        const name = doc.event_name || '';
        const isDuplicate = seen.has(name);
        if (name && !isDuplicate) {
          seen.add(name);
          return true;
        }
        return false;
      });
      
      setSuggestions(unique);
      setIsShowingSuggestions(unique.length > 0);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
      setSearchError('Failed to fetch suggestions');
      setIsShowingSuggestions(false);
    }
  }, [buildSearchParams, language, type, startDateString, endDateString]);

  // Create debounced version of getSuggestions
  const getSuggestionsDebounced = useMemo(() =>
    debounce((value) => {
      if (value.length < 2) {
        setSuggestions([]);
        setIsShowingSuggestions(false);
        return;
      }
      getSuggestions(value);
    }, 300),
  [getSuggestions]);

  // Clean up debounce on unmount
  useEffect(() => () => {
    if (getSuggestionsDebounced && getSuggestionsDebounced.cancel) {
      getSuggestionsDebounced.cancel();
    }
  }, [getSuggestionsDebounced]);

  // Handle suggestion selection
  const onSuggestionSelected = (event, { suggestion }) => {
    if (suggestion && suggestion.id) {
      setSearchTerm('');
      setIsShowingSuggestions(false);
      navigate(`/event/${suggestion.id}`);
    }
  };

  // Autosuggest handlers
  const onSuggestionsFetchRequested = ({ value }) => {
    try {
      getSuggestionsDebounced(value);
    } catch (error) {
      console.error('Error in suggestion fetch:', error);
      setSuggestions([]);
      setIsShowingSuggestions(false);
    }
  };

  const onSuggestionsClearRequested = () => {
    // Keep suggestions visible until selection or explicit clear
  };

  // Render suggestion
  const renderSuggestion = (suggestion) => {
    const displayName = suggestion.displayed_name || suggestion.event_name || '';
    return <div style={autosuggestStyles.suggestion}>{displayName}</div>;
  };

  // Get suggestion value for display
  const getSuggestionValue = (suggestion) => {
    return suggestion.displayed_name || suggestion.event_name || '';
  };

  // Handler functions for filters
  const handleTypeChange = (e) => {
    setType(e.target.value);
  };

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
  };

  const handleLanguageChange = (newLanguage) => {
    changeLanguage(newLanguage);
    onExplicitUiLangSwitch(newLanguage);
    // optionally refresh local suggestions list so it reflects the new language:
    if (searchTerm) {
      // re-run suggestions with current term in the new language
      getSuggestionsDebounced(searchTerm);
    }
  };

  // Don't render header if branding should be hidden
  if (branding.hide) {
    return null;
  }

  return (
    <HeaderContainer>
      <HeaderContent>
        <LogoImage
          src={branding.brand.logoSrc}
          alt={`${branding.brand.name} Logo`}
          onClick={handleLogoClick}
          aria-label="Navigate to home page"
        />

        
        <SearchContainer>
          <Autosuggest
            suggestions={suggestions}
            onSuggestionsFetchRequested={onSuggestionsFetchRequested}
            onSuggestionsClearRequested={onSuggestionsClearRequested}
            onSuggestionSelected={onSuggestionSelected}
            getSuggestionValue={getSuggestionValue}
            renderSuggestion={renderSuggestion}
            inputProps={{
              placeholder: language === 'TH' ? 'ค้นหากิจกรรม' : language === 'CN' ? '搜索活动' : 'Search for events',
              value: searchTerm,
              onChange: (_, { newValue }) => {
                setSearchTerm(newValue);
                if (newValue === '') {
                  setSuggestions([]);
                  setIsShowingSuggestions(false);
                }
              },
              style: autosuggestStyles.input,
              'aria-label': 'Search events'
            }}
            theme={{
              ...autosuggestStyles,
              suggestionsContainer: {
                ...autosuggestStyles.suggestionsContainer,
                display: isShowingSuggestions && suggestions.length > 0 ? 'block' : 'none',
              }
            }}
            highlightFirstSuggestion={true}
            alwaysRenderSuggestions={false}
          />
          
          {searchTerm && (
            <IconButton
              size="small"
              onClick={clearSearchTerm}
              sx={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#888',
                padding: '2px',
                zIndex: 2
              }}
              aria-label="Clear search"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </IconButton>
          )}
          
          {searchError && (
            <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5, position: 'absolute' }}>
              {searchError}
            </Box>
          )}
        </SearchContainer>
        
        <ControlsContainer>
          <FormControl sx={{ minWidth: 150, borderRadius: '20px' }}>
            <Select
              value={type}
              onChange={handleTypeChange}
              size="small"
              fullWidth
              sx={{ 
                borderRadius: '20px',
                height: '40px',
                bgcolor: 'white',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#ccc',
                },
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                fontFamily: "'Montserrat', 'Poppins', sans-serif",
              }}
            >
              {eventTypes.EN.map((value, i) => (
                <MenuItem key={i} value={value} sx={{ fontFamily: "'Montserrat', 'Poppins', sans-serif" }}>
                  {eventTypes[language][i]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            value={startDateString}
            onChange={handleStartDateChange}
            type="date"
            size="small"
            sx={{ 
              width: 145, 
              bgcolor: 'white',
              borderRadius: '20px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '20px',
                height: '40px',
                '& fieldset': {
                  borderColor: '#ccc',
                },
              },
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              fontFamily: "'Montserrat', 'Poppins', sans-serif",
            }}
            InputLabelProps={{ shrink: true }}
          />
          
          <TextField
            value={endDateString}
            onChange={handleEndDateChange}
            type="date"
            size="small"
            sx={{ 
              width: 145, 
              bgcolor: 'white',
              borderRadius: '20px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '20px',
                height: '40px',
                '& fieldset': {
                  borderColor: '#ccc',
                },
              },
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              fontFamily: "'Montserrat', 'Poppins', sans-serif",
            }}
            InputLabelProps={{ shrink: true }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <FlagButton 
              onClick={() => handleLanguageChange('TH')} 
              aria-label="Thai language"
              isActive={language === 'TH'}
            >
              <img src={ThaiFlag} alt="TH" width={24} />
            </FlagButton>
            <FlagButton 
              onClick={() => handleLanguageChange('EN')} 
              aria-label="English language"
              isActive={language === 'EN'}
            >
              <img src={EnglishFlag} alt="EN" width={24} />
            </FlagButton>
            <FlagButton 
              onClick={() => handleLanguageChange('CN')} 
              aria-label="Chinese language"
              isActive={language === 'CN'}
            >
              <img src={ChinaFlag} alt="CN" width={24} />
            </FlagButton>
          </Box>
        </ControlsContainer>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default EventDetailHeader;